import { Modal } from '@trackman/web-shared-components';
import './LearnMoreDialog.scss';
import { Trans, useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import Subtitle from 'components/TextComps/Subtitle';
import { ReactComponent as Medal } from 'images/icons/MedalThin.svg';
import { ReactComponent as Checkmark } from 'images/icons/CheckmarkThin.svg';
import { ShopCertificate } from 'types/pricing';
import { CartItem } from 'types/cart';
import Paragraph from 'components/TextComps/Paragraph';
import { ReactComponent as CheckmarkCircle } from 'images/icons/CheckmarkCircle.svg';

type Props = {
  open: boolean;
  certification: ShopCertificate;
  isItemInCart: boolean;
  isItemLocked: boolean;
  onClose: () => void;
  onAddToCart?: () => void;
};

export const LearnMoreDialog = ({ open, certification, isItemInCart, isItemLocked, onClose, onAddToCart }: Props) => {
  const { t } = useTranslation();

  const handleAddToCart = useCallback(() => {
    if (onAddToCart) {
      onAddToCart();
    }
  }, []);

  return (
    <Modal
      className='certificate-learn-more-modal'
      cancelBtn={{
        label: t('Close', { ns: 'subscriptionsAndPricing' }),
        onClick: onClose,
      }}
      confirmBtn={
        isItemLocked
          ? undefined
          : isItemInCart
            ? {
                label: t('AddedToCart', { ns: 'subscriptionsAndPricing' }),
                className: 'added-to-cart-button',
                startIcon: <CheckmarkCircle className='added-to-cart-button-icon' />,
              }
            : {
                label: t('AddToCart', { ns: 'subscriptionsAndPricing' }),
                onClick: handleAddToCart,
              }
      }
      onClose={onClose}
      title={t('CertificateOverview', { ns: 'subscriptionsAndPricing' })}
      open={open}
    >
      <div className='certification-learn-more-header'>
        <Paragraph>
          <>{t('CertificationExam', { ns: 'subscriptionsAndPricing' })}</>
        </Paragraph>
        <div>
          <Medal />
          <Subtitle>
            <>{certification.title}</>
          </Subtitle>
        </div>
        <Paragraph>
          <>{certification.shortDescription}</>
        </Paragraph>
      </div>

      <div className='certification-learn-more-content'>
        <div className='certification-learn-more-section'>
          <Subtitle>
            <>{t('AboutTheCertification', { ns: 'subscriptionsAndPricing' })}</>
          </Subtitle>
          <Trans
            i18nKey='AboutTheCertificationDescriptionLine1'
            ns='subscriptionsAndPricing'
            values={{ certificateSubject: 'xyz', certificatePurpose: 'xyz' }}
            defaults='This is an exam about {{certificateSubject}}. We recommend this exam for coaches that want to {{certificatePurpose}}.'
            components={{ bold: <strong /> }}
          ></Trans>

          <p>
            <Trans
              i18nKey='AboutTheCertificationDescriptionLine2'
              ns='subscriptionsAndPricing'
              values={{ certificateTitle: certification.title }}
              defaults='The best way to prepare for the {{certificateTitle}} certification is to <bold>go through all the learning materials of the course and complete the chapter tests</bold> (Only available for Premium members).'
              components={{ bold: <strong /> }}
            ></Trans>
          </p>
        </div>

        <div className='certification-learn-more-section'>
          <Subtitle>
            <>{t('WhatYouWillGet', { ns: 'subscriptionsAndPricing' })}</>
          </Subtitle>
          <div className='certification-learn-more-benefits'>
            <div className='certification-learn-more-benefit-item'>
              <Checkmark className='benefit-icon' />
              {t('Benefit1', { ns: 'subscriptionsAndPricing' })}
            </div>

            <div className='certification-learn-more-benefit-item'>
              <Checkmark className='benefit-icon' />
              {t('Benefit2', { ns: 'subscriptionsAndPricing' })}
            </div>

            <div className='certification-learn-more-benefit-item'>
              <Checkmark className='benefit-icon' />
              {t('Benefit3', { ns: 'subscriptionsAndPricing' })}
            </div>
          </div>
        </div>

        <div className='certification-learn-more-section'>
          <Subtitle>
            <>{t('ExamDetails', { ns: 'subscriptionsAndPricing' })}</>
          </Subtitle>
          <ul className='certification-learn-more-details'>
            <li>
              <Trans
                i18nKey='ExamDetail1'
                ns='subscriptionsAndPricing'
                defaults='$400 fee - <bold>50% off with Premium</bold>'
                components={{ bold: <strong className='premium-info' /> }}
              ></Trans>
            </li>
            <li>{t('ExamDetail2', { ns: 'subscriptionsAndPricing' })}</li>
            <li>{t('ExamDetail3', { ns: 'subscriptionsAndPricing' })}</li>
            <li>{t('ExamDetail4', { ns: 'subscriptionsAndPricing' })}</li>
            <li>{t('ExamDetail5', { ns: 'subscriptionsAndPricing' })}</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};
